import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useAuth } from "../../firebase/AuthProvider";
import { useTranslation } from "react-i18next";
import { Link } from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Grid from "@material-ui/core/Grid";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import TextField from "@material-ui/core/TextField";
import { useForm } from "react-hook-form";
import { emailRegex } from "../../constants";
import Spinner from "../shared/Spinner";
import Button from "../shared/Button";
import { colors } from "../../theme";
import DefaultBackground from "./svg/register-background.svg";

const useStyles = makeStyles((theme) => ({
  modal: {
    width: "600px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    }
  },
  header: {
    paddingTop: "5px",
    paddingBottom: "10px",
    backgroundImage: `url(${DefaultBackground})`,
    width: "100%",
    textAlign: "center"
  },
  title: {
    marginTop: "10px",
    fontSize: "35px",
    color: colors.SLOGAN_TEXT,
    fontWeight: 900,
    lineHeight: "2.13",
    letterSpacing: "0.08px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "25px",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "22px",
    }
  },
  signintitle: {
    textAlign: "center"
  },
  form: {
    margin: theme.spacing(3),
    marginTop: "0",
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    width: "140px",
  },
  error: {
    color: colors.ERROR,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  link:  {
    color:colors.LINK
  },
  input: {
    width: "450px",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      marginLeft: "10px"
    }
  },
  centered: {
    textAlign: "center"
  }
}));

export interface SignInDialogProps {
  open: boolean;
  onClose: () => void;
}

interface Credentials {
  email: string;
  password: string;
}

export default function SignInDialog({ onClose, open }: SignInDialogProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Keeping the form's state and doing validation is done with react-hook-form's useForm hook.
  const { register, handleSubmit, errors } = useForm<Credentials>();

  const handleClose = () => {
    onClose();
  };

  async function signInClick({ email, password }: Credentials) {
    setError(false);
    setIsLoading(true);

    let close = false;
    try {
      await signIn(email, password);
      close = true;
    } catch (reason) {
      setError(true);
    } finally {
      setIsLoading(false);
    }

    if (close) handleClose();
  }

  return (
    <Dialog onClose={handleClose} open={open}>
      <div className={classes.modal}>
        <div className={classes.header}>
          <Typography component="h1" className={classes.title}>
            {t("signin.firstsignin")}
          </Typography>
        </div>
        <DialogTitle id="signin-dialog-title" className={classes.signintitle}>
          {t("header.signin")}
          <IconButton className={classes.closeButton} onClick={onClose} style={{ backgroundColor: 'transparent' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Spinner isVisible={isLoading} />

        <Grid container item direction="column" justify="center" alignItems="center">
          <form
            className={classes.form}
            noValidate
            onSubmit={handleSubmit(signInClick)}
          >
            {error && (
              <Grid item>
                <Typography>
                  <div className={classes.error}>
                    <div>{t("signin.invalidcredentials")}</div>
                  </div>
                </Typography>
              </Grid>
            )}

            <Grid item>
              <TextField
                className={classes.input}
                variant="outlined"
                margin="normal"
                required
                id="email"
                label={t("signin.email")}
                name="email"
                autoComplete="email"
                autoFocus
                inputRef={register({
                  required: true,
                  maxLength: 255,
                  pattern: emailRegex,
                })}
                error={errors.email ? true : false}
                helperText={errors.email ? t("signin.email_error") : null}
              />
            </Grid>

            <Grid item>
              <TextField
                className={classes.input}
                variant="outlined"
                margin="normal"
                required
                name="password"
                label={t("signin.password")}
                type="password"
                id="password"
                autoComplete="current-password"
                inputRef={register({ required: true })}
                error={errors.password ? true : false}
                helperText={errors.password ? t("signin.password_error") : null}
              />
            </Grid>

            <Grid item>
              <Link component={RouterLink} to="/forgot-password" variant="body2" className={classes.link}>
                {t("signin.forgotpassword")}
              </Link>
            </Grid>

            <Grid item xs={12} className={classes.centered}>
              <Button type="submit" className={classes.submit}>
                {t("signin.submit")}
              </Button>
            </Grid>
            <Grid item xs={12} className={classes.centered}>
                <Link component={RouterLink} to="/signup" variant="body2" className={classes.link}>
                  {t("signin.noaccountyet")}
                </Link>
              </Grid>
          </form>
        </Grid>
      </div>
    </Dialog>
  );
}
